/* =======================================================
   SHARED AJAX FETCH + SWAP
   Used by subcategory pills and toggle switches so they
   update the grid/filter bar without a full page reload —
   mirrors what the native facet-form (collection.js) does
   for the Farbe/Größe drawer.
======================================================= */
const CK_SWAP_TARGET_IDS = ['ck-collection-filters', 'ProductGridContainer', 'ProductCount', 'SortByContainer'];

function ckFetchAndSwap(url) {
  const container = document.getElementById('ck-collection-filters');
  const targetUrl = new URL(url, window.location.href);

  if (container) {
    container.classList.add('loading');
  }

  const fetchUrl = new URL(targetUrl);
  if (container && window.theme && theme.utils && theme.utils.sectionId) {
    try {
      fetchUrl.searchParams.set('section_id', theme.utils.sectionId(container));
    } catch (err) {
      // console.warn('[CK Filters] Could not resolve section id:', err);
    }
  }

  fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    .then((res) => res.text())
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      CK_SWAP_TARGET_IDS.forEach((id) => {
        const current = document.getElementById(id);
        const next = doc.getElementById(id);
        if (current && next) current.innerHTML = next.innerHTML;
      });

      const cleanUrl = new URL(targetUrl);
      cleanUrl.searchParams.delete('section_id');
      history.pushState({}, '', cleanUrl.toString());

      document.dispatchEvent(new CustomEvent('collection:reloaded'));
      ckReinitFilters();
    })
    .catch((err) => {
      console.warn('[CK Filters] Failed to update filters:', err);
    })
    .finally(() => {
      const stillThere = document.getElementById('ck-collection-filters');
      if (stillThere) stillThere.classList.remove('loading');
    });
}

/* =======================================================
   SUBCATEGORIES FILTER (Search & Discovery: ck_collection_filter)
======================================================= */
function initCkSubmenuFilters() {
  const submenuBtns = document.querySelectorAll('.ck-submenu-btn[href]');
  if (!submenuBtns.length) return;

  submenuBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      ckFetchAndSwap(btn.href);
    });
  });
}

/* =======================================================
   TOGGLE FILTER (Search & Discovery: ck_toggle_filter)
======================================================= */
function initCkFilters() {
  const toggles = document.querySelectorAll('.ck-filter[data-filter-value]');
  if (!toggles.length) return;

  const PARAM = 'filter.p.m.custom.ck_toggle_filter';

  toggles.forEach((toggle) => {
    toggle.addEventListener('change', () => {
      const value = toggle.dataset.filterValue;
      if (!value) return;

      const url = new URL(window.location.href);
      const current = url.searchParams.getAll(PARAM);
      url.searchParams.delete(PARAM);

      const updated = toggle.checked
        ? (current.includes(value) ? current : [...current, value])
        : current.filter((v) => v !== value);

      updated.forEach((v) => url.searchParams.append(PARAM, v));
      ckFetchAndSwap(url.toString());
    });
  });
}

/* =======================================================
   DYNAMIC DRAWER + COUNT + BADGE HANDLING
======================================================= */
function initCkBadges() {
  const countEl =
    document.querySelector('.ck-collection-filters__count') ||
    document.querySelector('.collection-product-count') ||
    document.querySelector('.product-count') ||
    document.querySelector('.collection-count');

  const drawer = document.querySelector('#FacetDrawer');
  if (!drawer) return;

  function getActiveFacets() {
    const facets = {};
    const facetGroups = drawer.querySelectorAll('[data-filter-handle]');

    facetGroups.forEach((group) => {
      const handle = group.dataset.filterHandle;
      const checkedInputs = group.querySelectorAll('input[type="checkbox"]:checked');

      if (checkedInputs.length > 0) {
        facets[handle] = Array.from(checkedInputs).map((input) => input.value);
      }
    });

    return facets;
  }

  function updateBadges() {
    const activeFacets = getActiveFacets();

    const buttons = document.querySelectorAll('.ck-filter-btn[data-filter-handle]');
    buttons.forEach((btn) => {
      const handle = btn.dataset.filterHandle;
      const activeValues = activeFacets[handle] || [];
      let badge = btn.querySelector('.ck-filter-count__wrapper');
      let span = badge ? badge.querySelector('.ck-filter-count') : null;

      if (activeValues.length > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'ck-filter-count__wrapper';
          span = document.createElement('span');
          span.className = 'ck-filter-count';
          badge.appendChild(span);
          btn.appendChild(badge);
        }

        span.textContent = activeValues.length;

        badge.classList.add('active');
        btn.classList.add('active');
      } else {
        if (badge) badge.remove();
        btn.classList.remove('active');
      }
    });
  }

  drawer.addEventListener('toggle', async () => {
    const isOpen = drawer.hasAttribute('open');

    if (!isOpen) {
      try {
        const res = await fetch(window.location.href, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newCountEl =
          doc.querySelector('.ck-collection-filters__count') ||
          doc.querySelector('.collection-product-count') ||
          doc.querySelector('.product-count') ||
          doc.querySelector('.collection-count');

        if (newCountEl && countEl) {
          const newText = newCountEl.textContent.trim();
          countEl.textContent = newText;
        }

        updateBadges();
      } catch (err) {
        console.warn('[CK Filters] ⚠️ Failed to update counts:', err);
      }
    }
  });

  drawer.addEventListener('change', (e) => {
    if (e.target.matches('input[type="checkbox"]')) {
      updateBadges();
    }
  });

  updateBadges();
}

/* =======================================================
   FACET DRAWER: open/switch panels, titles, reset buttons
   (one click handler drives all three — they were three
   separate listeners doing overlapping work before)
======================================================= */
function initCkFacetDrawer() {
  const drawer = document.querySelector('#FacetDrawer');
  if (!drawer) return;

  const form = drawer.querySelector('form.facet-form');
  const buttons = document.querySelectorAll('.ck-filter-btn[data-filter-handle]');
  const facets = drawer.querySelectorAll('.ck-details[data-filter-handle]');
  const titles = drawer.querySelectorAll('.drawer__title[data-filter-handle]');
  const resetButtons = drawer.querySelectorAll('.ck-reset-filters');

  function hasSelectedFilters(facet) {
    return (
      facet &&
      facet.querySelectorAll('input[type="checkbox"]:checked').length > 0
    );
  }

  function updateResetButtons() {
    const activeFacet = drawer.querySelector('.ck-details.active[data-filter-handle]');
    const activeHandle = activeFacet?.dataset.filterHandle;

    resetButtons.forEach((btn) => {
      const isVisible =
        activeHandle === btn.dataset.filterHandle && hasSelectedFilters(activeFacet);
      btn.classList.toggle('active', isVisible);
      btn.classList.toggle('hidden', !isVisible);
    });
  }

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const handle = btn.dataset.filterHandle;

      drawer.setAttribute('open', '');

      facets.forEach((facet) => facet.classList.remove('active'));
      const activeFacet = drawer.querySelector(`.ck-details[data-filter-handle="${handle}"]`);
      if (activeFacet) activeFacet.classList.add('active');

      titles.forEach((title) => title.classList.remove('active'));
      const activeTitle = drawer.querySelector(`.drawer__title[data-filter-handle="${handle}"]`);
      if (activeTitle) activeTitle.classList.add('active');

      updateResetButtons();
    });
  });

  drawer.addEventListener('toggle', () => {
    if (!drawer.hasAttribute('open')) {
      titles.forEach((title) => title.classList.remove('active'));
    }
  });

  drawer.addEventListener('change', (e) => {
    if (e.target.matches('input[type="checkbox"]')) {
      updateResetButtons();
    }
  });

  drawer.addEventListener('click', (e) => {
    const btn = e.target.closest('.ck-reset-filters.active');
    if (!btn) return;

    e.preventDefault();
    const handle = btn.dataset.filterHandle;

    const facetGroup = drawer.querySelector(
      `.ck-details[data-filter-handle="${handle}"]`
    );
    if (facetGroup) {
      const checked = facetGroup.querySelectorAll('input[type="checkbox"]:checked');
      checked.forEach((cb) => (cb.checked = false));
    }

    if (!btn.classList.contains('ck-resetting')) {
      btn.classList.add('ck-resetting');
      const original = btn.textContent.trim();
      btn.textContent = `${original} …`;

      setTimeout(() => {
        btn.textContent = original;
        btn.classList.remove('ck-resetting');
      }, 1500);
    }

    if (form?.hasAttribute('update-on-change')) {
      form.dispatchEvent(new Event('input', { bubbles: true }));
      form.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      form.submit();
    }

    updateResetButtons();
  });

  updateResetButtons();
}

/* =======================================================
   DRAG ON DESKTOP
======================================================= */
function initCkDragScroll() {
  const scrollers = document.querySelectorAll('.ck-collection-filters__submenu');

  scrollers.forEach((scroller) => {
    let isDown = false;
    let startX;
    let scrollLeft;

    scroller.addEventListener('mousedown', (e) => {
      isDown = true;
      scroller.classList.add('dragging');
      startX = e.pageX - scroller.offsetLeft;
      scrollLeft = scroller.scrollLeft;
    });

    scroller.addEventListener('mouseleave', () => {
      isDown = false;
      scroller.classList.remove('dragging');
    });

    scroller.addEventListener('mouseup', () => {
      isDown = false;
      scroller.classList.remove('dragging');
    });

    scroller.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - scroller.offsetLeft;
      const walk = (x - startX) * 1;
      scroller.scrollLeft = scrollLeft - walk;
    });
  });
}

/* =======================================================
   TOGGLES TOOLTIPS
======================================================= */
function initCkTooltips() {
  const infos = document.querySelectorAll('.ck-filter-info');
  const wrapper = document.querySelector('.ck-collection-filters__toggles');

  infos.forEach((info) => {
    info.addEventListener('mouseenter', () => {
      if (window.matchMedia('(hover: hover)').matches) {
        info.classList.add('active');
      }
    });

    info.addEventListener('mouseleave', () => {
      info.classList.remove('active');
      wrapper?.classList.remove('active');
    });

    info.addEventListener('click', (e) => {
      if (!window.matchMedia('(hover: hover)').matches) {
        e.preventDefault();

        infos.forEach((el) => el.classList.remove('active'));

        const isActive = info.classList.toggle('active');
        if (isActive) {
          wrapper?.classList.add('active');
        } else {
          wrapper?.classList.remove('active');
        }
      }
    });
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('.ck-filter-info')) {
      infos.forEach((el) => el.classList.remove('active'));
      wrapper?.classList.remove('active');
    }
  });
}

/* =======================================================
   INIT / RE-INIT
   ckReinitFilters() re-runs every listener setup so the
   filter bar keeps working after its markup is replaced
   by ckFetchAndSwap().
======================================================= */
function ckReinitFilters() {
  initCkSubmenuFilters();
  initCkFilters();
  initCkBadges();
  initCkFacetDrawer();
  initCkDragScroll();
  initCkTooltips();
}

document.addEventListener('DOMContentLoaded', ckReinitFilters);
window.addEventListener('pageshow', ckReinitFilters);

/* Theme editor re-renders this section's markup on setting changes */
document.addEventListener('shopify:section:load', ckReinitFilters);
